/**
 * Optimistic list staging, as a reusable mutation lifecycle (#2018 item 5).
 *
 * ── what this is, and what it deliberately is not ──────────────────────────
 * `stageOptimisticActivity` proved a pattern: put the submitted row at the head
 * of every cached list already on screen, remember the exact prior answers, and
 * either replace the temporary id with the server's or restore what was there.
 * That pattern is generic. The KNOWLEDGE of which caches an activity touches,
 * and what an activity row looks like, is not — it belongs to the composer.
 *
 * So this lifts the mechanism and leaves the domain where it is. A helper that
 * also knew about tasks and meeting reports would be the composer with a
 * different name, and the next caller would have to widen it.
 *
 * ── the two rules the packet makes mandatory ───────────────────────────────
 * ROLLBACK IS EXACT. `rollback()` restores the snapshot, not "the list minus
 * the optimistic row" — those differ whenever a refetch landed in between, and
 * subtracting would leave the newer answer with a hole in it.
 *
 * ANNOUNCEMENT IS NOT OPTIONAL. Rolling back silently is the failure mode this
 * is meant to prevent: the row vanishes and the person is told nothing, so they
 * retype it. Announcement already exists globally — `client.tsx` reports every
 * write failure through the mutation cache unless the caller sets
 * `meta.errorHandled`, which means it says so beside the affordance instead. A
 * caller that rolls back and sets `errorHandled` without rendering a refusal is
 * the one shape that loses the error, and it is called out here because nothing
 * else in the type can catch it.
 *
 * ── only a LIST is staged ──────────────────────────────────────────────────
 * `stage` returns without doing anything when the cached answer is not an
 * array. A query that has never been fetched has no prior answer to restore, so
 * staging into it would invent a list the server never sent and leave a row
 * that no rollback can remove.
 */
import type { QueryClient, QueryKey } from '@tanstack/react-query'
import type { Row } from '@solum/sdk'

/** The prior answer for one cached list, kept so rollback can be exact. */
type Snapshot = readonly [QueryKey, Row[] | undefined]

export interface OptimisticRows {
  /** Put `row` at the head of one cached list, if that list is already loaded. */
  stage(key: QueryKey, row: Row): void
  /**
   * Stage into every cached list under a key PREFIX.
   *
   * The same row can be on screen in several scoped copies of one list — a
   * timeline filtered per participant, for instance — and they are separate
   * cache entries. `keep` decides which of them this row actually belongs in;
   * without it a scoped list would show a row its own filter excludes.
   */
  stageMatching(prefix: QueryKey, row: Row, keep?: (key: QueryKey) => boolean): void
  /** Replace the temporary id with the server's, merging `settled` into the row. */
  commit(serverId: string, settled?: Row): void
  /** Restore the exact prior answers. */
  rollback(): void
  /** The keys this staged into — for tests and for callers that must reconcile. */
  readonly staged: readonly QueryKey[]
}

/**
 * Begin an optimistic staging session against `optimisticId`.
 *
 * The id is the caller's, not generated here: it is usually needed before this
 * is called (to build the row) and again after (to reconcile), and a helper
 * that minted its own would force the caller to read it back out.
 */
export function optimisticRows(qc: QueryClient, optimisticId: string): OptimisticRows {
  const snapshots: Snapshot[] = []
  const staged: QueryKey[] = []

  const put = (key: QueryKey, row: Row, previous: Row[] | undefined) => {
    if (!Array.isArray(previous)) return
    snapshots.push([key, previous])
    staged.push(key)
    qc.setQueryData<Row[]>(key, [row, ...previous])
  }

  return {
    staged,

    stage(key, row) {
      put(key, row, qc.getQueryData<Row[]>(key))
    },

    stageMatching(prefix, row, keep) {
      for (const [key, previous] of qc.getQueriesData<Row[]>({ queryKey: prefix })) {
        if (keep && !keep(key)) continue
        put(key, row, previous)
      }
    },

    commit(serverId, settled) {
      for (const key of staged) {
        qc.setQueryData<Row[]>(key, (rows) => rows?.map((row) => (
          row.id === optimisticId || row.activity_id === optimisticId
            ? {
              ...row,
              ...settled,
              id: serverId,
              /* Only if the row carried one. Writing `activity_id` onto a row
                 that never had it would invent a column the grid may render. */
              activity_id: row.activity_id ? serverId : undefined,
              optimistic: false,
            }
            : row)))
      }
    },

    rollback() {
      for (const [key, previous] of snapshots) qc.setQueryData(key, previous)
    },
  }
}
